import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { SimpleChanges } from '../../utils/types';
import { Pagination } from '../../woo_services.module/PaginationService';

// Number of pages rendered on either side of the current one
const WINDOW_RADIUS = 2;

@Component({
  selector: 'pagination',
  templateUrl: './pagination.component.html',
  styleUrls: ['./pagination.component.scss'],
  standalone: false,
})
export class PaginationComponent implements OnChanges {
  @Input() pagination: Pagination;
  @Input() css: string;
  @Output() pageSelected = new EventEmitter<number>();

  items: PageItem[] = [];

  ngOnChanges(changes: SimpleChanges<PaginationComponent>): void {
    if (changes.pagination && this.pagination) {
      this.items = this.buildWindow(this.pagination.page, this.pagination.last);
    }
  }

  get rangeStart(): number {
    return (this.pagination.page - 1) * this.pagination.perPage + 1;
  }

  get rangeEnd(): number {
    return Math.min(this.rangeStart + this.pagination.perPage - 1, this.pagination.total);
  }

  requestPage = (page: number): void => {
    if (page < 1 || page > this.pagination.last || page === this.pagination.page) return;
    this.pageSelected.emit(page);
  };

  trackByItem(_: number, item: PageItem): string {
    return item.kind === 'page' ? `p${item.number}` : `g${item.from}`;
  }

  private buildWindow(page: number, last: number, radius: number = WINDOW_RADIUS): PageItem[] {
    const items: PageItem[] = [];
    const lo = Math.max(2, page - radius);
    const hi = Math.min(last - 1, page + radius);

    const pushSpan = (from: number, to: number): void => {
      if (to < from) return;
      if (from === to) items.push({ kind: 'page', number: from });
      else items.push({ kind: 'gap', from, to });
    };

    items.push({ kind: 'page', number: 1 });
    pushSpan(2, lo - 1);
    for (let n = lo; n <= hi; n++) items.push({ kind: 'page', number: n });
    pushSpan(hi + 1, last - 1);
    if (last > 1) items.push({ kind: 'page', number: last });

    return items;
  }
}

export type PageItem = { kind: 'page'; number: number } | { kind: 'gap'; from: number; to: number };
